import { normalizeToggles, type Action, type MeOutput } from '@garage/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { api } from '../../shared/api.js';
import { keys } from './keys.js';

/**
 * Utilisateur connecté avec un garage actif, fourni par l'espace garage (`/g/:slug/…`) aux écrans.
 * L'interface n'est qu'un reflet des droits renvoyés par `auth.me` : l'API vérifie chaque action.
 *
 * Vague 5 : pour une **session support** (administrateur plateforme entré avec un accès accordé),
 * `active` est reconstruit depuis `me.support` (droits `supportPermissions`, marque du garage) et
 * `supportSession` est renseigné ; l'administrateur n'a aucune appartenance dans ce garage.
 */
export type ActiveMe = MeOutput & {
  active: NonNullable<MeOutput['active']>;
  supportSession?: NonNullable<MeOutput['support']> | null;
};

/** Contexte « garage actif » d'une session support : droits et marque viennent de `me.support`. */
export function supportActiveMe(me: MeOutput): ActiveMe | null {
  const s = me.support;
  if (!s) return null;
  return {
    ...me,
    active: {
      // Aucune appartenance : jamais assigné, jamais « moi ».
      membershipId: '',
      garageName: s.garageName,
      role: s.scope === 'full' ? 'partner' : 'readonly',
      toggles: normalizeToggles('readonly', undefined),
      permissions: s.permissions,
      branding: s.branding,
      garageSlug: s.garageSlug,
      supportAccess: { id: s.accessId, scope: s.scope, amountsVisible: s.amountsVisible, grantedUntil: s.grantedUntil },
      // Intégration vague 7 : modules du garage visité, renvoyés par la session support.
      enabledModules: s.enabledModules,
    },
    supportSession: s,
  };
}

const MeContext = createContext<ActiveMe | null>(null);

export function MeProvider({ me, children }: { me: ActiveMe; children: ReactNode }) {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

export function useActiveMe(): ActiveMe {
  const me = useContext(MeContext);
  if (!me) throw new Error('useActiveMe hors de l’espace garage');
  return me;
}

/** Utilisateur actif s'il existe (composants utilisables hors de l'espace garage). */
export function useOptionalActiveMe(): ActiveMe | null {
  return useContext(MeContext);
}

/** Session support en cours (administrateur plateforme), sinon `null`. */
export function useSupportSession(): NonNullable<MeOutput['support']> | null {
  return useContext(MeContext)?.supportSession ?? null;
}

/** Fuseau du garage (réglages), pour afficher les échéances comme le garage les a fixées. */
export function useGarageTimezone(): string | undefined {
  const settings = useQuery({ queryKey: keys.settings, queryFn: () => api('garage.getSettings'), staleTime: 5 * 60_000 });
  return settings.data?.timezone;
}

/** Droit de l'utilisateur dans le garage actif (affichage seulement). */
export function useCan(): (action: Action) => boolean {
  const me = useActiveMe();
  return (action) => me.active.permissions.includes(action);
}
